import { useState } from "react";
import { da } from "date-fns/locale";
import { format } from "date-fns";
import { CalendarDays, ChevronDown, Clock3 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const pad = (value) => String(value).padStart(2, "0");
const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => `${pad(Math.floor(index / 2))}:${pad((index % 2) * 30)}`);
const tomorrow = () => new Date(Date.now() + 86400000);
const formatDateTime = (value) => new Intl.DateTimeFormat("da-DK", {
  weekday: "long", day: "numeric", month: "long", year: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: false,
}).format(new Date(value));

export default function AgreementScheduling({ problem, onUpdated, className = "" }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(tomorrow());
  const [time, setTime] = useState("14:00");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const state = problem.consultation_schedule_state || "no_schedule";
  const proposedByMe = problem.schedule_proposed_by === user?.id;
  const counterpart = user?.role === "expert" ? "kunden" : "eksperten";

  const update = async (payload, name) => {
    setBusy(name); setError("");
    try {
      const { data } = await api.post(`/problems/${problem.id}/schedule`, payload);
      setEditing(false); onUpdated?.(data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Tidspunktet kunne ikke opdateres.");
    } finally { setBusy(""); }
  };
  const proposeTime = () => {
    const value = new Date(date); const [hours, minutes] = time.split(":").map(Number);
    value.setHours(hours, minutes, 0, 0);
    if (value <= new Date()) { setError("Vælg en dato og et tidspunkt i fremtiden."); return; }
    update({ action: "propose", availability_type: "scheduled", proposed_time: value.toISOString() }, "time");
  };

  if (problem.payment_status !== "paid") return null;

  if (state === "confirmed" && !editing) {
    return <div className={`mt-3 ${className}`} data-testid="agreement-scheduling-secondary">
      <button type="button" onClick={() => setEditing(true)} className="text-sm font-medium text-brand-fresh hover:underline">
        Foreslå andet tidspunkt
      </button>
    </div>;
  }

  return <section className={`rounded-2xl border border-brand-line bg-brand-wash p-5 ${className}`} data-testid="agreement-scheduling">
    {state === "proposed" && !editing ? <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[.08em] text-brand-fresh">Foreslået tidspunkt</div>
          <h3 className="mt-1 font-display text-xl font-semibold text-brand-ink">
            {formatDateTime(problem.schedule_proposed_at)}
          </h3>
          {proposedByMe && <p className="mt-2 text-sm text-brand-muted">Afventer svar fra {counterpart}.</p>}
        </div>
        {!proposedByMe && <button type="button" onClick={() => update({ action: "accept", availability_type: problem.schedule_proposal_type || "scheduled", proposed_time: null }, "accept")} disabled={!!busy} className="btn-primary">
          {busy === "accept" ? "Accepterer…" : "Accepter tidspunkt"}
        </button>}
      </div>
      <button type="button" onClick={() => setEditing(true)} className="mt-4 text-sm font-medium text-brand-fresh hover:underline">Foreslå andet tidspunkt</button>
    </> : <>
      <div>
        <h3 className="font-display text-[17px] font-semibold text-brand-ink">{state === "no_schedule" ? "Aftal dato og tidspunkt" : "Foreslå andet tidspunkt"}</h3>
        <p className="mt-1 text-sm text-brand-muted">Forslaget skal accepteres af {counterpart}, før videosamtalen er bekræftet.</p>
      </div>
      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Popover><PopoverTrigger asChild><button className="flex min-h-12 w-full items-center gap-2 rounded-lg border border-brand-line bg-white px-3 text-left text-sm"><CalendarDays size={15}/><span className="flex-1">{format(date, "d. MMMM yyyy", { locale: da })}</span><ChevronDown size={15}/></button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" locale={da} selected={date} onSelect={(value) => value && setDate(value)} disabled={{ before: new Date() }} initialFocus/></PopoverContent></Popover>
        <label className="relative flex min-h-12 cursor-pointer items-center gap-2 rounded-lg border border-brand-line bg-white px-3"><Clock3 size={15}/><span className="flex-1 text-sm">{time}</span><ChevronDown size={15}/><select value={time} onChange={(event) => setTime(event.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label="Tidspunkt">{TIME_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></label>
      </div>
      <button type="button" onClick={proposeTime} disabled={!!busy} className="mt-2 min-h-11 w-full rounded-lg bg-brand-fresh text-sm font-semibold text-white disabled:opacity-50">{busy === "time" ? "Sender forslag…" : "Foreslå dato og tidspunkt"}</button>
      {editing && <button type="button" onClick={() => setEditing(false)} className="mt-3 text-sm text-[#4D6E64] hover:underline">Annuller</button>}

    </>}
    {error && <div className="mt-3 text-sm text-[#991B1B]" role="alert">{error}</div>}
  </section>;
}

